import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';

export type LifeEvent = {
  id: string;
  description: string;
  cost: number;
  details: string;
  eventDate: string;
  clientId: string;
};

type UseLifeEventsReturnType = {
  isLoading: boolean;
  lifeEventsData: LifeEvent[] | undefined;
  upsertLifeEvents: (items: LifeEvent[]) => Promise<void>;
  deleteLifeEvents: (ids: string[]) => Promise<void>;
};

export default function useLifeEvents(): UseLifeEventsReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();

  const queryKey = ['clients', clientId, 'life-events'];

  const { data, isLoading } = useQuery<LifeEvent[]>({
    queryKey,
  });

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: LifeEvent[]) => api.put(`clients/${clientId}/life-events/batch`, items),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertLifeEvents = async (items: LifeEvent[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/life-events/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteLifeEvents = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  return {
    isLoading,
    lifeEventsData: data,
    upsertLifeEvents,
    deleteLifeEvents,
  };
}
